import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft, Truck, Loader2, Save, FileDown, Pencil, X, Plus, Package, FolderPlus, ArrowRight,
    Mail, Phone, MapPin, StickyNote, Clock, Send, PackageCheck, Image as ImageIcon, BadgeCheck,
} from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { resolveLogoUrl } from '../../lib/resolveLogoUrl';
import { ADDRESS_TYPES } from '../../lib/addressTypes';
import { generateSupplierPdf } from '../../lib/supplierPdfGenerator';
import { Button } from '../ui/Button';
import { Input, Textarea } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { NotificationBlock, insertNotificationToken, wrapNotificationSelection } from '../PrestationPlanning/PrestationNotificationsPage';

// Jetons propres au mail de commande fournisseur — {{date_reception_souhaitee}} et {{lien_confirmation}}
// ne sont pertinents que pour ce mail précis (calculés uniquement au moment de l'envoi).
const SUPPLIER_ORDER_TOKENS = [
    { token: '{{entreprise}}', label: 'Nom de votre établissement' },
    { token: '{{fournisseur}}', label: 'Nom du fournisseur' },
    { token: '{{articles}}', label: 'Articles commandés' },
    { token: '{{date_reception_souhaitee}}', label: 'Date de réception souhaitée (si renseignée)' },
    { token: '{{date}}', label: 'Date de la démarche (RDV ou question, si connue)' },
    { token: '{{heure}}', label: 'Heure de la démarche (RDV ou question, si connue)' },
    { token: '{{date_debut}}', label: 'Date de début' },
    { token: '{{heure_debut}}', label: 'Heure de début' },
    { token: '{{date_fin}}', label: 'Date de fin (si connue)' },
    { token: '{{heure_fin}}', label: 'Heure de fin (si connue)' },
    { token: '{{montant_regle}}', label: 'Montant déjà réglé par le client' },
    { token: '{{montant_total}}', label: 'Montant total de la démarche' },
    { token: '{{reste_a_payer}}', label: 'Reste à payer par le client' },
    { token: '{{lien_confirmation}}', label: 'Lien de confirmation par le fournisseur' },
    { token: '{{logo}}', label: 'Votre logo' },
];

// Jetons propres au mail de confirmation envoyé au CLIENT FINAL une fois que ce fournisseur a
// confirmé sa commande (lien ou confirmation manuelle).
const CLIENT_CONFIRMATION_TOKENS = [
    { token: '{{prenom}}', label: 'Prénom du contact' },
    { token: '{{nom}}', label: 'Nom (ou raison sociale) du contact' },
    { token: '{{entreprise}}', label: 'Nom de votre établissement' },
    { token: '{{articles}}', label: 'Articles commandés' },
    { token: '{{date}}', label: 'Date de la démarche (RDV ou question, si connue)' },
    { token: '{{heure}}', label: 'Heure de la démarche (RDV ou question, si connue)' },
    { token: '{{date_debut}}', label: 'Date de début' },
    { token: '{{heure_debut}}', label: 'Heure de début' },
    { token: '{{date_fin}}', label: 'Date de fin (si connue)' },
    { token: '{{heure_fin}}', label: 'Heure de fin (si connue)' },
    { token: '{{montant_regle}}', label: 'Montant déjà réglé par le client' },
    { token: '{{montant_total}}', label: 'Montant total de la démarche' },
    { token: '{{reste_a_payer}}', label: 'Reste à payer par le client' },
    { token: '{{logo}}', label: 'Votre logo' },
];

// Jetons propres au mail reçu par L'ÉTABLISSEMENT (pas le client) quand ce fournisseur confirme sa
// commande (lien ou confirmation manuelle).
const ESTABLISHMENT_CONFIRMATION_TOKENS = [
    { token: '{{entreprise}}', label: 'Nom de votre établissement' },
    { token: '{{fournisseur}}', label: 'Nom du fournisseur' },
    { token: '{{articles}}', label: 'Articles commandés' },
    { token: '{{client_nom}}', label: 'Nom du client ayant commandé (si renseigné)' },
    { token: '{{date}}', label: 'Date de la démarche (RDV ou question, si connue)' },
    { token: '{{heure}}', label: 'Heure de la démarche (RDV ou question, si connue)' },
    { token: '{{date_debut}}', label: 'Date de début' },
    { token: '{{heure_debut}}', label: 'Heure de début' },
    { token: '{{date_fin}}', label: 'Date de fin (si connue)' },
    { token: '{{heure_fin}}', label: 'Heure de fin (si connue)' },
    { token: '{{montant_regle}}', label: 'Montant déjà réglé par le client' },
    { token: '{{montant_total}}', label: 'Montant total de la démarche' },
    { token: '{{reste_a_payer}}', label: 'Reste à payer par le client' },
    { token: '{{logo}}', label: 'Votre logo' },
];

interface SupplierArticle {
    id: string;
    label: string;
    price_ttc: number;
    vat_rate: number;
    category_id: string | null;
}

interface SupplierCategory {
    id: string;
    name: string;
    article_count: number;
    order_count: number;
}

interface Supplier {
    id: string;
    name: string;
    email: string;
    phone: string;
    address_number?: string | null;
    address_type?: string | null;
    address_name?: string | null;
    address_city?: string | null;
    address_postal_code?: string | null;
    notes: string;
    contact_first_name?: string | null;
    contact_last_name?: string | null;
    contact_phone?: string | null;
    contact_email?: string | null;
    logo_url?: string | null;
    order_email_subject?: string | null;
    order_email_body?: string | null;
    client_confirmation_subject?: string | null;
    client_confirmation_body?: string | null;
    establishment_confirmation_subject?: string | null;
    establishment_confirmation_body?: string | null;
    articles: SupplierArticle[];
    categories: SupplierCategory[];
}

const MAX_LOGO_DATA_URL_LENGTH = 1000000;

export const SupplierDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [supplier, setSupplier] = React.useState<Supplier | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [notFound, setNotFound] = React.useState(false);

    const [isEditing, setIsEditing] = React.useState(false);
    const [name, setName] = React.useState('');
    const [email, setEmail] = React.useState('');
    const [phone, setPhone] = React.useState('');
    const [addressNumber, setAddressNumber] = React.useState('');
    const [addressType, setAddressType] = React.useState('');
    const [addressName, setAddressName] = React.useState('');
    const [addressCity, setAddressCity] = React.useState('');
    const [addressPostalCode, setAddressPostalCode] = React.useState('');
    const [notes, setNotes] = React.useState('');
    const [contactFirstName, setContactFirstName] = React.useState('');
    const [contactLastName, setContactLastName] = React.useState('');
    const [contactPhone, setContactPhone] = React.useState('');
    const [contactEmail, setContactEmail] = React.useState('');
    const [logoUrl, setLogoUrl] = React.useState<string | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');

    const [orderCounts, setOrderCounts] = React.useState<{ pending: number; toOrder: number; ordered: number; confirmed: number } | null>(null);

    const [showAddCategory, setShowAddCategory] = React.useState(false);
    const [newCategoryName, setNewCategoryName] = React.useState('');
    const [savingCategory, setSavingCategory] = React.useState(false);

    const [showEmailSettings, setShowEmailSettings] = React.useState(false);
    const [emailSettingsTab, setEmailSettingsTab] = React.useState<'fournisseur' | 'client' | 'etablissement'>('fournisseur');
    const [orderEmailSubject, setOrderEmailSubject] = React.useState('');
    const [orderEmailBody, setOrderEmailBody] = React.useState('');
    const [clientConfirmationSubject, setClientConfirmationSubject] = React.useState('');
    const [clientConfirmationBody, setClientConfirmationBody] = React.useState('');
    const [establishmentConfirmationSubject, setEstablishmentConfirmationSubject] = React.useState('');
    const [establishmentConfirmationBody, setEstablishmentConfirmationBody] = React.useState('');
    const [savingOrderEmail, setSavingOrderEmail] = React.useState(false);
    const [orderEmailActiveField, setOrderEmailActiveField] = React.useState<'subject' | 'body'>('body');
    const orderEmailSubjectRef = React.useRef<HTMLInputElement>(null);
    const orderEmailBodyRef = React.useRef<HTMLTextAreaElement>(null);
    const insertOrderEmailToken = (token: string) => {
        if (orderEmailActiveField === 'subject') insertNotificationToken(orderEmailSubjectRef.current, orderEmailSubject, setOrderEmailSubject, token);
        else insertNotificationToken(orderEmailBodyRef.current, orderEmailBody, setOrderEmailBody, token);
    };
    const wrapOrderEmailBody = (marker: string) => wrapNotificationSelection(orderEmailBodyRef.current, orderEmailBody, setOrderEmailBody, marker);
    const [clientConfirmationActiveField, setClientConfirmationActiveField] = React.useState<'subject' | 'body'>('body');
    const clientConfirmationSubjectRef = React.useRef<HTMLInputElement>(null);
    const clientConfirmationBodyRef = React.useRef<HTMLTextAreaElement>(null);
    const insertClientConfirmationToken = (token: string) => {
        if (clientConfirmationActiveField === 'subject') insertNotificationToken(clientConfirmationSubjectRef.current, clientConfirmationSubject, setClientConfirmationSubject, token);
        else insertNotificationToken(clientConfirmationBodyRef.current, clientConfirmationBody, setClientConfirmationBody, token);
    };
    const wrapClientConfirmationBody = (marker: string) => wrapNotificationSelection(clientConfirmationBodyRef.current, clientConfirmationBody, setClientConfirmationBody, marker);
    const [establishmentConfirmationActiveField, setEstablishmentConfirmationActiveField] = React.useState<'subject' | 'body'>('body');
    const establishmentConfirmationSubjectRef = React.useRef<HTMLInputElement>(null);
    const establishmentConfirmationBodyRef = React.useRef<HTMLTextAreaElement>(null);
    const insertEstablishmentConfirmationToken = (token: string) => {
        if (establishmentConfirmationActiveField === 'subject') insertNotificationToken(establishmentConfirmationSubjectRef.current, establishmentConfirmationSubject, setEstablishmentConfirmationSubject, token);
        else insertNotificationToken(establishmentConfirmationBodyRef.current, establishmentConfirmationBody, setEstablishmentConfirmationBody, token);
    };
    const wrapEstablishmentConfirmationBody = (marker: string) => wrapNotificationSelection(establishmentConfirmationBodyRef.current, establishmentConfirmationBody, setEstablishmentConfirmationBody, marker);

    const load = React.useCallback(() => {
        if (!id) return;
        moduleApi.getSupplier(id).then((s: any) => {
            if (!s) { setNotFound(true); return; }
            setSupplier(s);
            setName(s.name || '');
            setEmail(s.email || '');
            setPhone(s.phone || '');
            setAddressNumber(s.address_number || '');
            setAddressType(s.address_type || '');
            setAddressName(s.address_name || '');
            setAddressCity(s.address_city || '');
            setAddressPostalCode(s.address_postal_code || '');
            setNotes(s.notes || '');
            setContactFirstName(s.contact_first_name || '');
            setContactLastName(s.contact_last_name || '');
            setContactPhone(s.contact_phone || '');
            setContactEmail(s.contact_email || '');
            setLogoUrl(s.logo_url || null);
            setOrderEmailSubject(s.order_email_subject || '');
            setOrderEmailBody(s.order_email_body || '');
            setClientConfirmationSubject(s.client_confirmation_subject || '');
            setClientConfirmationBody(s.client_confirmation_body || '');
            setEstablishmentConfirmationSubject(s.establishment_confirmation_subject || '');
            setEstablishmentConfirmationBody(s.establishment_confirmation_body || '');
        }).catch(() => setNotFound(true)).finally(() => setLoading(false));
    }, [id]);

    React.useEffect(() => { load(); }, [load]);

    React.useEffect(() => {
        if (!id) return;
        moduleApi.getSupplierOrders(id).then((d: any) => {
            // "commandees" mélange envoyées-en-attente et confirmées (confirmed_at) — même split que
            // SupplierOrdersPage.tsx/CommandesModule.tsx, sinon "Commandées" comptait aussi les confirmées
            // et la carte "Confirmées" manquait entièrement (signalé par Emeni, 2026-08-20).
            const commandees = Array.isArray(d?.commandees) ? d.commandees : [];
            setOrderCounts({
                pending: Array.isArray(d?.en_attente_paiement) ? d.en_attente_paiement.length : 0,
                toOrder: Array.isArray(d?.a_commander) ? d.a_commander.length : 0,
                ordered: commandees.filter((o: any) => !o.confirmed_at).length,
                confirmed: commandees.filter((o: any) => !!o.confirmed_at).length,
            });
        }).catch(() => setOrderCounts({ pending: 0, toOrder: 0, ordered: 0, confirmed: 0 }));
    }, [id]);

    const startEdit = () => {
        if (!supplier) return;
        setName(supplier.name || '');
        setEmail(supplier.email || '');
        setPhone(supplier.phone || '');
        setAddressNumber(supplier.address_number || '');
        setAddressType(supplier.address_type || '');
        setAddressName(supplier.address_name || '');
        setAddressCity(supplier.address_city || '');
        setAddressPostalCode(supplier.address_postal_code || '');
        setNotes(supplier.notes || '');
        setContactFirstName(supplier.contact_first_name || '');
        setContactLastName(supplier.contact_last_name || '');
        setContactPhone(supplier.contact_phone || '');
        setContactEmail(supplier.contact_email || '');
        setLogoUrl(supplier.logo_url || null);
        setError('');
        setIsEditing(true);
    };

    const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const base64 = ev.target?.result as string;
            if (base64.length > MAX_LOGO_DATA_URL_LENGTH) {
                setError('Logo trop volumineux (max 1 Mo).');
                return;
            }
            setLogoUrl(base64);
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const handleSave = async () => {
        if (!id || !name.trim()) { setError('Le nom est requis.'); return; }
        setSaving(true);
        setError('');
        try {
            await moduleApi.updateSupplier(id, {
                name: name.trim(), email: email.trim(), phone: phone.trim(),
                address_number: addressNumber.trim(), address_type: addressType.trim(),
                address_name: addressName.trim(), address_city: addressCity.trim(),
                address_postal_code: addressPostalCode.trim(),
                notes: notes.trim(), logo_url: logoUrl || '',
                contact_first_name: contactFirstName.trim(), contact_last_name: contactLastName.trim(),
                contact_phone: contactPhone.trim(), contact_email: contactEmail.trim()
            });
            setIsEditing(false);
            load();
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'enregistrement.");
        } finally {
            setSaving(false);
        }
    };

    const handleSaveOrderEmail = async () => {
        if (!id) return;
        setSavingOrderEmail(true);
        setError('');
        try {
            await moduleApi.updateSupplier(id, {
                order_email_subject: orderEmailSubject, order_email_body: orderEmailBody,
                client_confirmation_subject: clientConfirmationSubject, client_confirmation_body: clientConfirmationBody,
                establishment_confirmation_subject: establishmentConfirmationSubject, establishment_confirmation_body: establishmentConfirmationBody,
            });
            setShowEmailSettings(false);
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'enregistrement.");
        } finally {
            setSavingOrderEmail(false);
        }
    };

    const formatAddress = (num: string, type: string, n: string, city: string, postal: string) => {
        return [num, type, n, postal, city].filter(Boolean).join(' ');
    };

    const handleDownloadPdf = () => {
        if (!id || !supplier) return;
        const currentAddressNumber = isEditing ? addressNumber : supplier.address_number;
        const currentAddressType = isEditing ? addressType : supplier.address_type;
        const currentAddressName = isEditing ? addressName : supplier.address_name;
        const currentAddressCity = isEditing ? addressCity : supplier.address_city;
        const currentAddressPostalCode = isEditing ? addressPostalCode : supplier.address_postal_code;
        generateSupplierPdf({
            id, name: (isEditing ? name : supplier.name).trim() || 'Fournisseur',
            email: isEditing ? email : supplier.email,
            phone: isEditing ? phone : supplier.phone,
            address: formatAddress(currentAddressNumber || '', currentAddressType || '', currentAddressName || '', currentAddressCity || '', currentAddressPostalCode || ''),
            notes: isEditing ? notes : supplier.notes,
            logoUrl: (isEditing ? logoUrl : supplier.logo_url) ? resolveLogoUrl((isEditing ? logoUrl : supplier.logo_url) as string) : null,
            // Le catalogue de la fiche liste les CATÉGORIES, pas les articles : même parti pris que la fiche
            // partenaire, qui liste les types d'extras. Demande d'Emeni (2026-08-24).
            // Les articles sans catégorie sont signalés explicitement : sinon ils disparaîtraient de la
            // fiche sans que rien ne l'indique, alors qu'ils existent bien chez ce fournisseur.
            categories: [
                ...(supplier.categories || []).map((cat: any) => cat.name),
                ...((supplier.articles || []).some((a: any) => !a.category_id) ? ['Sans catégorie'] : []),
            ],
            contact: {
                firstName: isEditing ? contactFirstName : supplier.contact_first_name || '',
                lastName: isEditing ? contactLastName : supplier.contact_last_name || '',
                email: isEditing ? contactEmail : supplier.contact_email || '',
                phone: isEditing ? contactPhone : supplier.contact_phone || '',
            },
            issuerName: user?.company_name || undefined,
            brandColor: user?.theme_color || undefined,
        });
    };

    const handleAddCategory = async () => {
        if (!id || !newCategoryName.trim()) return;
        setSavingCategory(true);
        try {
            await moduleApi.createSupplierCategory(id, { name: newCategoryName.trim() });
            setNewCategoryName('');
            setShowAddCategory(false);
            load();
        } catch (e: any) {
            setError(e?.message || "Impossible de créer cette catégorie.");
        } finally {
            setSavingCategory(false);
        }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;
    if (notFound || !supplier) {
        return (
            <div className="text-center">
                <p className="text-content-muted text-sm mb-4">Fournisseur introuvable.</p>
                <Button variant="secondary" onClick={() => navigate('/fournisseurs')}><ArrowLeft size={15} /> Retour</Button>
            </div>
        );
    }

    const totalArticles = supplier.articles.length;
    const uncategorizedCount = supplier.articles.filter((a) => !a.category_id).length;

    return (
        <div className="space-y-6 max-w-5xl">
            <button onClick={() => navigate('/fournisseurs')} className="flex items-center gap-1.5 text-sm text-content-muted hover:text-content font-medium">
                <ArrowLeft size={15} /> Fournisseurs
            </button>

            {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-6 items-start">
                <div className="space-y-4">
                    {/* Carte de présentation (lecture seule) / formulaire d'édition */}
                    <div className="bg-surface rounded-2xl border border-border p-6">
                        {!isEditing ? (
                            <>
                                <div className="flex items-start justify-between gap-3 mb-5">
                                    <div className="w-16 h-16 shrink-0 rounded-xl bg-surface-soft border border-border overflow-hidden flex items-center justify-center">
                                        {supplier.logo_url ? (
                                            <img src={resolveLogoUrl(supplier.logo_url)} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <Truck className="text-content-muted" size={26} />
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                        <button onClick={handleDownloadPdf} title="Télécharger la fiche en PDF"
                                            className="p-2 rounded-lg border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-colors">
                                            <FileDown size={15} />
                                        </button>
                                        <button onClick={startEdit} title="Modifier la fiche"
                                            className="p-2 rounded-lg border border-blue-500/20 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors">
                                            <Pencil size={15} />
                                        </button>
                                    </div>
                                </div>
                                <h1 className="text-xl font-bold text-content mb-4 truncate">{supplier.name}</h1>
                                <div className="space-y-4">
                                    {/* Section 1: Infos de l'entité */}
                                    <div className="space-y-2.5 text-sm">
                                        <p className="text-xs uppercase tracking-widest text-content-muted font-bold">Infos de l'entité</p>
                                        <div className="flex items-center gap-2.5 text-content">
                                            <Mail size={14} className="text-content-muted shrink-0" />
                                            <span className="truncate">{supplier.email || 'Non renseigné'}</span>
                                        </div>
                                        <div className="flex items-center gap-2.5 text-content">
                                            <Phone size={14} className="text-content-muted shrink-0" />
                                            <span className="truncate">{supplier.phone || 'Non renseigné'}</span>
                                        </div>
                                        {(supplier.address_number || supplier.address_type || supplier.address_name || supplier.address_city || supplier.address_postal_code) && (
                                            <div className="flex items-start gap-2.5 text-content">
                                                <MapPin size={14} className="text-content-muted shrink-0 mt-0.5" />
                                                <span>{[supplier.address_number, supplier.address_type, supplier.address_name, supplier.address_postal_code, supplier.address_city].filter(Boolean).join(' ')}</span>
                                            </div>
                                        )}
                                        {supplier.notes && (
                                            <div className="flex items-start gap-2.5 text-content-muted pt-2 border-t border-border mt-2">
                                                <StickyNote size={14} className="shrink-0 mt-0.5" />
                                                <span className="text-xs">{supplier.notes}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Section 2: Infos du référent contact */}
                                    {(supplier.contact_first_name || supplier.contact_last_name || supplier.contact_phone || supplier.contact_email) && (
                                        <div className="space-y-2.5 text-sm pt-2 border-t border-border">
                                            <p className="text-xs uppercase tracking-widest text-content-muted font-bold">Référent contact</p>
                                            {(supplier.contact_first_name || supplier.contact_last_name) && (
                                                <div className="text-content">
                                                    <span className="font-semibold">{supplier.contact_first_name} {supplier.contact_last_name}</span>
                                                </div>
                                            )}
                                            {supplier.contact_email && (
                                                <div className="flex items-center gap-2.5 text-content">
                                                    <Mail size={14} className="text-content-muted shrink-0" />
                                                    <span className="truncate">{supplier.contact_email}</span>
                                                </div>
                                            )}
                                            {supplier.contact_phone && (
                                                <div className="flex items-center gap-2.5 text-content">
                                                    <Phone size={14} className="text-content-muted shrink-0" />
                                                    <span className="truncate">{supplier.contact_phone}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Stats Commandes intégrées à la fiche — cliquable, mène à la page Commandes */}
                                <button
                                    onClick={() => navigate(`/fournisseurs/${id}/commandes`)}
                                    className="w-full mt-4 pt-4 border-t border-border grid grid-cols-2 gap-y-3 gap-x-2 text-center hover:opacity-80 transition-opacity"
                                >
                                    <div>
                                        <p className="text-lg font-bold text-content flex items-center justify-center gap-1"><Clock size={13} className="text-content-muted" /> {orderCounts?.pending ?? '—'}</p>
                                        <p className="text-[10px] text-content-muted mt-0.5">En attente</p>
                                    </div>
                                    <div>
                                        <p className="text-lg font-bold text-content flex items-center justify-center gap-1"><Send size={13} className="text-content-muted" /> {orderCounts?.toOrder ?? '—'}</p>
                                        <p className="text-[10px] text-content-muted mt-0.5">À commander</p>
                                    </div>
                                    <div>
                                        <p className="text-lg font-bold text-content flex items-center justify-center gap-1"><PackageCheck size={13} className="text-content-muted" /> {orderCounts?.ordered ?? '—'}</p>
                                        <p className="text-[10px] text-content-muted mt-0.5">Commandées</p>
                                    </div>
                                    <div>
                                        <p className="text-lg font-bold text-content flex items-center justify-center gap-1"><BadgeCheck size={13} className="text-content-muted" /> {orderCounts?.confirmed ?? '—'}</p>
                                        <p className="text-[10px] text-content-muted mt-0.5">Confirmées</p>
                                    </div>
                                </button>
                            </>
                        ) : (
                            <div className="space-y-4">
                                <div className="flex items-center gap-4">
                                    <label className="w-16 h-16 shrink-0 rounded-xl bg-surface-soft border border-border overflow-hidden flex items-center justify-center cursor-pointer relative group">
                                        {logoUrl ? (
                                            <img src={resolveLogoUrl(logoUrl)} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <Truck className="text-content-muted" size={26} />
                                        )}
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <ImageIcon size={16} className="text-white" />
                                        </div>
                                        <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={handleLogoUpload} className="hidden" />
                                    </label>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs uppercase tracking-widest text-content-muted font-bold mb-1">Logo</p>
                                        <div className="flex items-center gap-2">
                                            <label className="text-xs font-bold text-brand-600 dark:text-brand-400 cursor-pointer hover:underline">
                                                Changer
                                                <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={handleLogoUpload} className="hidden" />
                                            </label>
                                            {logoUrl && (
                                                <button onClick={() => setLogoUrl(null)} className="text-xs font-bold text-red-400 hover:underline flex items-center gap-0.5">
                                                    <X size={12} /> Retirer
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {/* Section 1: Infos de l'entité */}
                                <div className="space-y-3 pb-4 border-b border-border">
                                    <p className="text-xs uppercase tracking-widest text-content-muted font-bold">Infos de l'entité</p>
                                    <div className="space-y-1.5">
                                        <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Nom *</label>
                                        <Input value={name} onChange={(e) => setName(e.target.value)} />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Email</label>
                                        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Téléphone</label>
                                        <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Adresse</label>
                                        <div className="grid grid-cols-[60px_1fr] gap-2">
                                            <Input placeholder="N°" value={addressNumber} onChange={(e) => setAddressNumber(e.target.value)} />
                                            <select value={addressType} onChange={(e) => setAddressType(e.target.value)}
                                                className="w-full bg-surface border border-border rounded-md text-content text-sm outline-none transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 px-2.5 py-2">
                                                <option value="">Type (rue, bld, chemin...)</option>
                                                {addressType && !ADDRESS_TYPES.includes(addressType) && <option value={addressType}>{addressType}</option>}
                                                {ADDRESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                        </div>
                                        <Input placeholder="Nom de la rue/avenue" value={addressName} onChange={(e) => setAddressName(e.target.value)} />
                                        <div className="grid grid-cols-[100px_1fr] gap-2">
                                            <Input placeholder="Code postal" value={addressPostalCode} onChange={(e) => setAddressPostalCode(e.target.value)} />
                                            <Input placeholder="Ville" value={addressCity} onChange={(e) => setAddressCity(e.target.value)} />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Notes</label>
                                        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Ex: délai de livraison habituel, conditions..." />
                                    </div>
                                </div>

                                {/* Section 2: Infos du référent contact */}
                                <div className="space-y-3 pt-4">
                                    <p className="text-xs uppercase tracking-widest text-content-muted font-bold">Référent contact</p>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1.5">
                                            <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Prénom</label>
                                            <Input value={contactFirstName} onChange={(e) => setContactFirstName(e.target.value)} />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Nom</label>
                                            <Input value={contactLastName} onChange={(e) => setContactLastName(e.target.value)} />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Email</label>
                                        <Input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs uppercase tracking-widest text-content-muted font-bold">Téléphone</label>
                                        <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 pt-1">
                                    <Button variant="secondary" onClick={() => setIsEditing(false)} disabled={saving} className="flex-1">
                                        <X size={15} /> Annuler
                                    </Button>
                                    <Button onClick={handleSave} disabled={saving} className="flex-1">
                                        {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {saving ? 'Enregistrement...' : 'Enregistrer'}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-surface rounded-2xl border border-border p-6 space-y-4">
                    <div className="flex items-center justify-between gap-3">
                        <p className="text-xs font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5"><Package size={13} /> Catalogue d'articles</p>
                        <button onClick={() => navigate(`/fournisseurs/${id}/articles`)} className="text-xs font-bold text-brand-600 dark:text-brand-400 hover:underline flex items-center gap-1 shrink-0">
                            Voir tous les articles <ArrowRight size={12} />
                        </button>
                    </div>

                    {totalArticles === 0 && supplier.categories.length === 0 ? (
                        <p className="text-xs text-content-muted">Aucun article ni catégorie pour l'instant.</p>
                    ) : (
                        <div className="grid grid-cols-2 gap-2.5">
                            {supplier.categories.map((cat) => (
                                <button key={cat.id} onClick={() => navigate(`/fournisseurs/${id}/articles/${cat.id}`)}
                                    className="text-left p-3.5 rounded-xl bg-surface-soft border border-border hover:border-brand-500/40 transition-colors">
                                    <p className="text-sm font-bold text-content truncate">{cat.name}</p>
                                    <p className="text-xs text-content-muted mt-0.5">{cat.article_count} article{cat.article_count > 1 ? 's' : ''} · {cat.order_count} commande{cat.order_count > 1 ? 's' : ''}</p>
                                </button>
                            ))}
                            {uncategorizedCount > 0 && (
                                <button onClick={() => navigate(`/fournisseurs/${id}/articles/none`)}
                                    className="text-left p-3.5 rounded-xl bg-surface-soft border border-dashed border-border hover:border-brand-500/40 transition-colors">
                                    <p className="text-sm font-bold text-content truncate">Sans catégorie</p>
                                    <p className="text-xs text-content-muted mt-0.5">{uncategorizedCount} article{uncategorizedCount > 1 ? 's' : ''}</p>
                                </button>
                            )}
                        </div>
                    )}

                    {showAddCategory ? (
                        <div className="flex items-center gap-1.5 pt-1">
                            <input value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} placeholder="Nom de la catégorie"
                                autoFocus
                                onKeyDown={(e) => { if (e.key === 'Enter') handleAddCategory(); if (e.key === 'Escape') setShowAddCategory(false); }}
                                className="flex-1 min-w-0 bg-surface border border-border rounded-md px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500" />
                            <Button size="sm" onClick={handleAddCategory} disabled={savingCategory || !newCategoryName.trim()}>
                                {savingCategory ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                            </Button>
                            <Button size="sm" variant="secondary" onClick={() => { setShowAddCategory(false); setNewCategoryName(''); }}><X size={13} /></Button>
                        </div>
                    ) : (
                        <Button variant="secondary" size="sm" onClick={() => setShowAddCategory(true)} className="w-full">
                            <FolderPlus size={14} /> Ajouter une catégorie
                        </Button>
                    )}
                </div>
            </div>

            <div className="max-w-3xl">
                <Button variant="secondary" onClick={() => setShowEmailSettings(true)}>
                    <Mail size={15} /> Paramétrer les emails de commande
                </Button>
            </div>

            <Modal
                open={showEmailSettings}
                onClose={() => setShowEmailSettings(false)}
                title="Paramétrer les emails de commande"
                icon={<Mail size={16} />}
                maxWidth="max-w-2xl"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowEmailSettings(false)}>Annuler</Button>
                        <Button onClick={handleSaveOrderEmail} disabled={savingOrderEmail}>
                            {savingOrderEmail ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {savingOrderEmail ? 'Enregistrement...' : 'Enregistrer'}
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div className="flex items-center gap-1.5 border-b border-border pb-2">
                        <button onClick={() => setEmailSettingsTab('fournisseur')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${emailSettingsTab === 'fournisseur' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                            Fournisseur
                        </button>
                        <button onClick={() => setEmailSettingsTab('client')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${emailSettingsTab === 'client' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                            Client
                        </button>
                        <button onClick={() => setEmailSettingsTab('etablissement')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${emailSettingsTab === 'etablissement' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                            Vous
                        </button>
                    </div>

                    {emailSettingsTab === 'fournisseur' ? (
                        <div className="space-y-2">
                            <p className="text-xs text-content-muted italic">Envoyé à ce fournisseur (bouton "Commander"). Laissez vide pour utiliser le modèle par défaut.</p>
                            <NotificationBlock title="Mail au fournisseur"
                                subject={orderEmailSubject} setSubject={setOrderEmailSubject} body={orderEmailBody} setBody={setOrderEmailBody}
                                subjectRef={orderEmailSubjectRef} bodyRef={orderEmailBodyRef}
                                activeField={orderEmailActiveField} setActiveField={setOrderEmailActiveField}
                                onInsertToken={insertOrderEmailToken} onWrap={wrapOrderEmailBody} tokens={SUPPLIER_ORDER_TOKENS} />
                        </div>
                    ) : emailSettingsTab === 'client' ? (
                        <div className="space-y-2">
                            <p className="text-xs text-content-muted italic">Envoyé au client final dès que son paiement est confirmé — uniquement si la démarche ne concernait que ce fournisseur (sinon, un mail consolidé est envoyé, réglable depuis la page "Fournisseurs"). Laissez vide pour utiliser le modèle par défaut.</p>
                            <NotificationBlock title="Mail au client final"
                                subject={clientConfirmationSubject} setSubject={setClientConfirmationSubject} body={clientConfirmationBody} setBody={setClientConfirmationBody}
                                subjectRef={clientConfirmationSubjectRef} bodyRef={clientConfirmationBodyRef}
                                activeField={clientConfirmationActiveField} setActiveField={setClientConfirmationActiveField}
                                onInsertToken={insertClientConfirmationToken} onWrap={wrapClientConfirmationBody} tokens={CLIENT_CONFIRMATION_TOKENS} />
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <p className="text-xs text-content-muted italic">Envoyé à VOUS (l'établissement) dès que ce fournisseur confirme avoir pris en compte sa commande. Laissez vide pour utiliser le modèle par défaut.</p>
                            <NotificationBlock title="Mail reçu quand le fournisseur confirme"
                                subject={establishmentConfirmationSubject} setSubject={setEstablishmentConfirmationSubject} body={establishmentConfirmationBody} setBody={setEstablishmentConfirmationBody}
                                subjectRef={establishmentConfirmationSubjectRef} bodyRef={establishmentConfirmationBodyRef}
                                activeField={establishmentConfirmationActiveField} setActiveField={setEstablishmentConfirmationActiveField}
                                onInsertToken={insertEstablishmentConfirmationToken} onWrap={wrapEstablishmentConfirmationBody} tokens={ESTABLISHMENT_CONFIRMATION_TOKENS} />
                        </div>
                    )}

                    {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
                </div>
            </Modal>
        </div>
    );
};
